import React from "react";

import type { StackLayer, StackRow } from "../utils/campaignStack";

/** A stack's legend under its chart, top layer first as the chart reads:
 *  each campaign's swatch, name and month total. */
export const StackLegend: React.FC<{
  layers: ReadonlyArray<StackLayer>;
  format: (n: number) => string;
}> = ({ layers, format }) => (
  <ul className="mt-2 space-y-1 text-[11px]">
    {[...layers].reverse().map((layer) => (
      <li key={layer.key} className="flex items-center gap-2 min-w-0">
        <span
          className="shrink-0 inline-block w-[8px] h-[8px] rounded-[2px]"
          style={{ backgroundColor: layer.color }}
        />
        <span className={`flex-1 min-w-0 truncate ${layer.other ? "text-gray-500" : "text-gray-700"}`}>
          {layer.name}
        </span>
        <span className="shrink-0 tabular-nums text-gray-700">{format(layer.total)}</span>
      </li>
    ))}
  </ul>
);

/** The hover card of a stacked chart: the day, each layer's value that day
 *  top first (a layer without the day is left out), then the total, and a
 *  last line the caller adds, such as last month's. */
export const StackTooltip: React.FC<{
  row: StackRow;
  layers: ReadonlyArray<StackLayer>;
  format: (n: number) => string;
  heading: string;
  totalLabel: string;
  footer?: React.ReactNode;
}> = ({ row, layers, format, heading, totalLabel, footer }) => {
  const shown = [...layers].reverse().filter((layer) => row.values[layer.key] != null);
  return (
    <div className="rounded-md bg-gray-900 text-white text-[11px] px-2 py-1.5 shadow-md tabular-nums max-w-[240px]">
      <div className="text-gray-300 mb-1">{heading}</div>
      {shown.map((layer) => (
        <div key={layer.key} className="flex items-center gap-1.5">
          <span
            className="shrink-0 inline-block w-[7px] h-[7px] rounded-[2px]"
            style={{ backgroundColor: layer.color }}
          />
          <span className="flex-1 min-w-0 truncate text-gray-200">{layer.name}</span>
          <span className="shrink-0 pl-2">{format(row.values[layer.key] as number)}</span>
        </div>
      ))}
      {shown.length > 1 && row.total != null && (
        <div className="flex justify-between gap-2 mt-0.5 pt-0.5 border-t border-white/15">
          <span className="text-gray-300">{totalLabel}</span>
          <span className="font-medium">{format(row.total)}</span>
        </div>
      )}
      {footer}
    </div>
  );
};
